import { useState, useEffect } from 'react';
import {
  AccountCircleOutlined,
  AccountBoxOutlined,
  ContactsOutlined,
  ClassOutlined,
  CategoryOutlined,
  TaskOutlined,
  GroupsOutlined,
  SegmentOutlined,
  PersonOutlined,
  BadgeOutlined,
  BookmarkBorderOutlined,
} from '@mui/icons-material';
import { ControlledTabDrawer, TabDrawerProps } from '@centerpiece/components';
import { ProjectStagesTab } from './tabs/ProjectStagesTab';
import { ItemStatusesTab } from './tabs/ItemStatusesTab';
import { RolesTab } from './tabs/RolesTab';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { ProjectTypesTab } from './tabs/ProjectTypesTab';
import { settingsSteps } from 'tutorials/settings.tutorial';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { TeamsTab } from './tabs/TeamsTab';
import { MarketSegmentsTab } from './tabs/MarketSegmentsTab';
import { ContactList } from 'components/ContactList';
import { AccountList } from 'components/AccountList';
import { StakeholdersTab } from './tabs/StakeholdersTab';
import { TagsTab } from './tabs/TagsTab';

export interface SettingsTabDrawerProps
  extends Omit<TabDrawerProps, 'tabs' | 'setTabs' | 'title' | 'status'> {}

export const SettingsTabDrawer = (props: SettingsTabDrawerProps) => {
  // useItemStatusesPrefetch({})
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  const typographyProps = {
    variant: 'body1',
    sx: { display: 'flex', lineHeight: 1 },
  } as any;

  const [activeTab, setActiveTab] = useState<string>('accounts');
  useEffect(() => {
    setActiveTab('accounts');
  }, [props.open]);
  const { state, setState } = useTutorialContext();

  useEffect(() => {
    if (props.open && state.tourActive) {
      setState(currState => ({
        ...currState,
        triggers: {
          ...currState.triggers,
          contactsTab: () => setActiveTab('contacts'),
          accountsTab: () => setActiveTab('accounts'),
        },
      }));
    }
    // eslint-disable-next-line
  }, [props.open, state.tourActive]);

  return (
    <ControlledTabDrawer
      onHelp={() =>
        setState(currState => ({
          run: true,
          steps: settingsSteps(),
          stepIndex: 0,
          triggers: {
            ...currState.triggers,
            contactsTab: () => setActiveTab('contacts'),
            accountsTab: () => setActiveTab('accounts'),
            close: () => {
              currState.triggers?.close() && currState.triggers.close();
            },
          },
        }))
      }
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      // width={{ xs: '100%' }}
      width={{ xs: '100%', lg: '75%' }}
      maxWidth={{ xs: '100%', sm: `calc(100% - 240px)` }}
      {...props}
      title={'Settings'}
      tabs={{
        accounts: {
          key: 'accounts',
          sequence: 1,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <AccountBoxOutlined fontSize="inherit" />
              &nbsp;{'Accounts'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <AccountList />,
        },
        // contacts: {
        //   key: 'contacts',
        //   sequence: 2,
        //   visible: true,
        //   icon: (
        //     <Typography {...typographyProps}>
        //       <ContactsOutlined fontSize="inherit" />
        //       &nbsp;{'Contacts'}
        //     </Typography>
        //   ),
        //   backgroundColor: 'white',
        //   // contents: <ContactsTab />,
        //   contents: <ContactList />,
        // },
        roles: {
          key: 'roles',
          sequence: 3,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <AccountCircleOutlined fontSize="inherit" />
              &nbsp;{'Roles'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <RolesTab />,
        },
        stakeholders: {
          key: 'stakeholders',
          sequence: 4,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <BadgeOutlined fontSize="inherit" />
              &nbsp;{'Stakeholders'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <StakeholdersTab />,
        },
        types: {
          key: 'types',
          sequence: 5,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <CategoryOutlined fontSize="inherit" />
              &nbsp;{'Types'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <ProjectTypesTab />,
        },
        states: {
          key: 'states',
          sequence: 6,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <ClassOutlined fontSize="inherit" />
              &nbsp;{'Project States & Stages'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <ProjectStagesTab />,
        },
        statuses: {
          key: 'statuses',
          sequence: 7,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <TaskOutlined fontSize="inherit" />
              &nbsp;{'Item Statuses'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <ItemStatusesTab />,
        },
        teams: {
          key: 'teams',
          sequence: 8,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <GroupsOutlined fontSize="inherit" />
              &nbsp;{'Teams'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <TeamsTab />,
        },
        tags: {
          key: 'tags',
          sequence: 9,
          visible: true,
          icon: (
            <Typography {...typographyProps}>
              <BookmarkBorderOutlined fontSize="inherit" />
              &nbsp;{'Item Tags'}
            </Typography>
          ),
          backgroundColor: 'white',
          contents: <TagsTab />,
        },
        // segments: {
        //   key: 'segments',
        //   sequence: 10,
        //   visible: true,
        //   icon: (
        //     <Typography {...typographyProps}>
        //       <SegmentOutlined fontSize="inherit" />
        //       &nbsp;{'Market Segments'}
        //     </Typography>
        //   ),
        //   backgroundColor: 'white',
        //   contents: <MarketSegmentsTab />,
        // },
      }}
      device={downSm ? 'mobile' : 'desktop'}
    />
  );
};
